/* ÜRETİLMİŞ DOSYA — ELLE DÜZENLEMEYİN.
   Kaynak: ris-api/RIS.UI/src/shared/template-builder
   Güncelleme: ris-api/scripts/sync-template-builder.sh */

import { Checkbox, Col, Form, Input, InputNumber, Row, Select, Upload, Button, Space, Alert } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import RichText from './RichText'

const ALIGN_OPTIONS = (t) => [
    { value: 'left', label: t('Sol') },
    { value: 'center', label: t('Orta') },
    { value: 'right', label: t('Sağ') },
    { value: 'justify', label: t('İki Yana Yasla') },
]

/** Serbest metin bloğu — gövdenin ana yapı taşı. */
export function TextBlock({ block, language, onChange, apiKey, registerInsert, t }) {
    return (
        <>
            <Form.Item label={t('Hizalama')} style={{ marginBottom: 8 }}>
                <Select
                    size="small"
                    style={{ width: 160 }}
                    value={block.align || 'left'}
                    onChange={(align) => onChange({ ...block, align })}
                    options={ALIGN_OPTIONS(t)}
                />
            </Form.Item>

            <RichText
                apiKey={apiKey}
                registerInsert={registerInsert}
                value={(block.html || {})[language] || ''}
                onChange={(html) => onChange({ ...block, html: { ...(block.html || {}), [language]: html } })}
            />
        </>
    )
}

/** Onay kutusu — onam formlarının temel ögesi (w14:checkbox olarak render edilir). */
export function CheckboxBlock({ block, language, onChange, placeholders, t }) {
    return (
        <Row gutter={8}>
            <Col span={14}>
                <Form.Item label={t('Etiket')} style={{ marginBottom: 8 }}>
                    <Input
                        size="small"
                        value={(block.label || {})[language] || ''}
                        onChange={(e) =>
                            onChange({ ...block, label: { ...(block.label || {}), [language]: e.target.value } })
                        }
                    />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item label={t('Değerden Doldur')} style={{ marginBottom: 8 }}>
                    <Select
                        size="small"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('(sabit)')}
                        value={block.key || undefined}
                        onChange={(key) => onChange({ ...block, key: key || '' })}
                        options={placeholders.map((p) => ({ value: p.token, label: p.label }))}
                    />
                </Form.Item>
            </Col>
            <Col span={4}>
                <Form.Item label={t('Varsayılan')} style={{ marginBottom: 8 }}>
                    <Checkbox
                        checked={!!block.checked}
                        onChange={(e) => onChange({ ...block, checked: e.target.checked })}
                    >
                        {t('İşaretli')}
                    </Checkbox>
                </Form.Item>
            </Col>
        </Row>
    )
}

/** Görsel: sabit dosya (logo) veya değerden gelen görsel (imza). */
export function ImageBlock({ block, onChange, placeholders, t }) {
    const imagePlaceholders = placeholders.filter((p) => p.kind === 'Image')

    const readFile = (file) => {
        const reader = new FileReader()
        reader.onload = () => onChange({ ...block, data: String(reader.result).split(',')[1] || '' })
        reader.readAsDataURL(file)
        return false
    }

    return (
        <>
            <Row gutter={8}>
                <Col span={8}>
                    <Form.Item label={t('Kaynak')} style={{ marginBottom: 8 }}>
                        <Select
                            size="small"
                            value={block.mode || 'asset'}
                            onChange={(mode) => onChange({ ...block, mode })}
                            options={[
                                { value: 'asset', label: t('Yüklenen dosya') },
                                { value: 'placeholder', label: t('Alandan gelen') },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Genişlik (cm)')} style={{ marginBottom: 8 }}>
                        <InputNumber
                            size="small"
                            min={0.5}
                            max={20}
                            step={0.5}
                            style={{ width: '100%' }}
                            value={block.width}
                            onChange={(width) => onChange({ ...block, width })}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Yükseklik (cm)')} style={{ marginBottom: 8 }}>
                        <InputNumber
                            size="small"
                            min={0.5}
                            max={25}
                            step={0.5}
                            style={{ width: '100%' }}
                            value={block.height}
                            onChange={(height) => onChange({ ...block, height })}
                        />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label={t('Hizalama')} style={{ marginBottom: 8 }}>
                        <Select
                            size="small"
                            value={block.align || 'center'}
                            onChange={(align) => onChange({ ...block, align })}
                            options={ALIGN_OPTIONS(t).slice(0, 3)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {block.mode === 'placeholder' ? (
                <Form.Item label={t('Alan')} style={{ marginBottom: 0 }}>
                    <Select
                        size="small"
                        style={{ width: 280 }}
                        value={block.key || undefined}
                        onChange={(key) => onChange({ ...block, key })}
                        options={imagePlaceholders.map((p) => ({ value: p.token, label: p.label }))}
                        placeholder={t('Görsel alanı seçin')}
                    />
                </Form.Item>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Upload accept="image/png,image/jpeg" showUploadList={false} beforeUpload={readFile}>
                        <Button size="small" icon={<UploadOutlined />}>{t('Görsel Yükle')}</Button>
                    </Upload>
                    {block.data ? (
                        <img
                            src={`data:image/png;base64,${block.data}`}
                            alt=""
                            style={{ maxHeight: 90, border: '1px solid #eee' }}
                        />
                    ) : (
                        <Alert type="info" showIcon message={t('Henüz görsel yüklenmedi')} />
                    )}
                </Space>
            )}
        </>
    )
}

/** İmza alanı — her slot bir sütun (imza görseli + isim + unvan + tarih). */
export function SignatureBlock({ block, language, onChange, placeholders, t }) {
    const slots = block.slots || []
    const setSlot = (index, patch) =>
        onChange({ ...block, slots: slots.map((s, i) => (i === index ? { ...s, ...patch } : s)) })

    const textOptions = placeholders.filter((p) => p.kind === 'Text').map((p) => ({ value: p.token, label: p.label }))
    const imageOptions = placeholders.filter((p) => p.kind === 'Image').map((p) => ({ value: p.token, label: p.label }))

    return (
        <>
            {slots.map((slot, index) => (
                <Row gutter={8} key={index} style={{ marginBottom: 6 }}>
                    <Col span={6}>
                        <Input
                            size="small"
                            placeholder={t('Unvan')}
                            value={(slot.title || {})[language] || ''}
                            onChange={(e) =>
                                setSlot(index, { title: { ...(slot.title || {}), [language]: e.target.value } })
                            }
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            size="small" allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
                            placeholder={t('İsim alanı')} value={slot.nameKey || undefined}
                            onChange={(nameKey) => setSlot(index, { nameKey: nameKey || '' })} options={textOptions}
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            size="small" allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
                            placeholder={t('İmza görseli')} value={slot.signKey || undefined}
                            onChange={(signKey) => setSlot(index, { signKey: signKey || '' })} options={imageOptions}
                        />
                    </Col>
                    <Col span={5}>
                        <Select
                            size="small" allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
                            placeholder={t('Tarih alanı')} value={slot.dateKey || undefined}
                            onChange={(dateKey) => setSlot(index, { dateKey: dateKey || '' })} options={textOptions}
                        />
                    </Col>
                    <Col span={1}>
                        <Button
                            size="small" danger
                            onClick={() => onChange({ ...block, slots: slots.filter((_, i) => i !== index) })}
                        >
                            ×
                        </Button>
                    </Col>
                </Row>
            ))}

            <Button
                size="small"
                onClick={() =>
                    onChange({ ...block, slots: [...slots, { title: {}, nameKey: '', signKey: '', dateKey: '' }] })
                }
            >
                {t('İmza Alanı Ekle')}
            </Button>
        </>
    )
}

/** Koşullu blok: alan doluysa/boşsa içindeki blokları göster. */
export function ConditionalHeader({ block, onChange, placeholders, t }) {
    const when = block.when || { key: '', op: 'notEmpty', value: '' }

    return (
        <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col span={9}>
                <Select
                    size="small" showSearch optionFilterProp="label" style={{ width: '100%' }}
                    placeholder={t('Alan')} value={when.key || undefined}
                    onChange={(key) => onChange({ ...block, when: { ...when, key } })}
                    options={placeholders.map((p) => ({ value: p.token, label: p.label }))}
                />
            </Col>
            <Col span={7}>
                <Select
                    size="small" style={{ width: '100%' }} value={when.op || 'notEmpty'}
                    onChange={(op) => onChange({ ...block, when: { ...when, op } })}
                    options={[
                        { value: 'notEmpty', label: t('doluysa') },
                        { value: 'isEmpty', label: t('boşsa') },
                        { value: 'equals', label: t('eşitse') },
                        { value: 'notEquals', label: t('eşit değilse') },
                    ]}
                />
            </Col>
            {(when.op === 'equals' || when.op === 'notEquals') && (
                <Col span={8}>
                    <Input
                        size="small" placeholder={t('Değer')} value={when.value || ''}
                        onChange={(e) => onChange({ ...block, when: { ...when, value: e.target.value } })}
                    />
                </Col>
            )}
        </Row>
    )
}

/** Boşluk bloğu. */
export function SpacerBlock({ block, onChange, t }) {
    return (
        <Form.Item label={t('Boş satır sayısı')} style={{ marginBottom: 0 }}>
            <InputNumber
                size="small" min={1} max={20} value={block.size || 1}
                onChange={(size) => onChange({ ...block, size })}
            />
        </Form.Item>
    )
}